import { isEqual, isNil, sortBy, uniqBy } from "lodash";
import AppContext from "PFApp/app_context";
import { useStorageTableColumns } from "PFApp/components/table/hooks/use_storage_table_columns";
import { WORKFLOW_STATES } from "PFApp/constants/workflow_states";
import { useFilteredCollection } from "PFApp/use_filtered_collection";
import { usePage } from "PFApp/use_page";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import useStorage from "PFCore/helpers/use_storage";
import { useActivityCounters, useActivityCountersInvalidate } from "PFCore/hooks/queries/workflow_inbox";
import { PermissionRule } from "PFTypes";
import { useContext, useEffect, useMemo, useState } from "react";

import { useInbox } from "../use_inbox";
import { blockedProperties } from "./roles_controls";
import { useRolesColumns } from "./use_roles_columns";

const insertAtIndex = (arr, index, elementsToInsert) => [
  ...arr.slice(0, index),
  ...elementsToInsert,
  ...arr.slice(index)
];

const useRolesTab = () => {
  const key = "workflow_roles_v2";
  const {
    store: { currentProfile }
  } = useContext(AppContext);

  const { defaultColumns, availableColumns } = useRolesColumns(blockedProperties);
  const { selectedColumns, setSelectedColumns } = useStorageTableColumns(
    `${key}_columns`,
    availableColumns,
    defaultColumns.filter(({ locked, workforceMemberView }) => locked && !workforceMemberView)
  );

  const [groupBy, setGroupBy] = useStorage(`${key}_group_by`, null);
  const [pinnedIds, setPinnedIds] = useStorage(`${key}_pinned`, []);
  const [isFirstTimeWMFilterView, setIsFirstTimeWMFilterView] = useStorage(
    `${key}_is_first_time_wm_view`,
    true
  );

  const [ganttView, setGanttView] = useStorage("showRolesGantt", false);

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const { page, mergePage } = usePage(`workflowPage`);

  const {
    activitiesWithNotifications: rolesWithNotifications,
    markAsRead,
    isLoading: notificationsLoading
  } = useInbox("role");

  const roles = useFilteredCollection(key, "/api/roles", {
    requestParams: { filters: { fields: { state: ["new"], workflow_state: Object.keys(WORKFLOW_STATES) } } }
  });
  const { loaded, data, noFiltersSelected, noOrderSelected, onOrderChange, fetchData } = roles;
  const orderOptions = data?.meta?.order?.options;

  const { data: countersData } = useActivityCounters({ templateKey: "role" }, { keepPreviousData: true });
  const { invalidate: invalidateCounters } = useActivityCountersInvalidate();

  const isWorkforceMemberFilterEnabled = useIsPermittedTo(currentProfile)(
    PermissionRule.FilterByWorkforceMember
  );
  // logic for the Workforce Member filter (used by Deloitte)
  // ---
  const isWorkforceMemberView = useMemo(
    () => !isNil(data.meta.filters.fields?.workforce_member_id?.value),
    [data.meta.filters]
  );

  useEffect(() => {
    if (isWorkforceMemberView && isWorkforceMemberFilterEnabled) {
      const workforceMemberViewColumns = sortBy(
        defaultColumns.filter(
          ({ workforceMemberView, name }) =>
            workforceMemberView || (isFirstTimeWMFilterView && ["start_date", "status"].includes(name))
        ),
        "workforceMemberView"
      );
      setSelectedColumns(uniqBy(insertAtIndex(selectedColumns, 1, workforceMemberViewColumns), "name"));
      onOrderChange(orderOptions.find(({ name }) => isEqual(name, ["activity.start_date"])));
      setIsFirstTimeWMFilterView(false);
    } else {
      setSelectedColumns(selectedColumns.filter(({ workforceMemberView }) => !workforceMemberView));
    }
  }, [isWorkforceMemberView, isWorkforceMemberFilterEnabled]);
  // ---

  const counters = useMemo(
    () => ({
      new: 0, // in case they come as null
      draft: 0,
      complete: 0,
      ...(countersData?.stateTally || {}),

      pending: 0, // in case they come as null
      shortlisting: 0,
      reviewing: 0,
      inviting: 0,
      partiallyFilled: 0,
      filled: 0,
      booked: 0,
      partiallyBooked: 0,
      confirmed: 0,
      partiallyConfirmed: 0,
      ...(countersData?.workflowStateTally || {})
    }),
    [countersData]
  );

  useEffect(() => {
    mergePage({
      roles: {
        ...page.roles,
        ...roles,
        columns: selectedColumns,
        setColumns: setSelectedColumns,
        fetchData: (params) => {
          fetchData(params);
          invalidateCounters({ templateKey: "role" });
        },
        ganttView,
        setGanttView,
        groupBy,
        setGroupBy,
        pinnedIds,
        setPinnedIds,
        selectMode,
        setSelectMode,
        selectedIds,
        setSelectedIds,
        unseen: {
          roles: rolesWithNotifications,
          loading: notificationsLoading,
          markAsRead
        },
        counters,
        isWorkforceMemberView
      }
    });
  }, [
    loaded,
    data,
    counters,
    noFiltersSelected,
    noOrderSelected,
    selectedColumns,
    ganttView,
    groupBy,
    pinnedIds,
    selectMode,
    selectedIds,
    rolesWithNotifications,
    notificationsLoading,
    isWorkforceMemberView
  ]);

  return page.roles;
};

export default useRolesTab;
